import { getDeviceId } from './deviceId';

const DB_NAME = 'HealthSurveyDB';
const DB_VERSION = 3; // Incremented to 3 to add the PHCs store
const SUBMISSIONS_STORE = 'submissions';
const QUESTIONS_STORE = 'questions';
const PHCS_STORE = 'phcs';

let db;

const initDB = () => {
  return new Promise((resolve, reject) => {
    // This is important: if the db exists but is a lower version,
    // onupgradeneeded will be triggered automatically.
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      reject('Database error: ' + event.target.error);
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      resolve(db);
    };

    // This event fires only if the database doesn't exist
    // or if the version number is higher than the existing one.
    request.onupgradeneeded = (event) => {
      // ...existing code...
      db = event.target.result;
      
      // Create submissions store if it doesn't exist
      if (!db.objectStoreNames.contains(SUBMISSIONS_STORE)) {
        const submissionsStore = db.createObjectStore(SUBMISSIONS_STORE, { keyPath: 'id', autoIncrement: true });
        submissionsStore.createIndex('synced', 'synced', { unique: false });
      }
      
      // Create questions store if it doesn't exist
      if (!db.objectStoreNames.contains(QUESTIONS_STORE)) {
        const questionsStore = db.createObjectStore(QUESTIONS_STORE, { keyPath: 'role' });
        questionsStore.createIndex('role', 'role', { unique: false });
      }

      // Create PHCs store if it doesn't exist
      if (!db.objectStoreNames.contains(PHCS_STORE)) {
        db.createObjectStore(PHCS_STORE, { keyPath: 'id' });
      }
    };
  });
};

// Helper function to ensure the store exists before a transaction
const checkStoreExists = (storeName) => {
  if (!db.objectStoreNames.contains(storeName)) {
    throw new Error(`Object store "${storeName}" not found. Please clear your browser's site data and refresh.`);
  }
};

// UPDATED: Save a complete submission offline
export const saveSubmissionOffline = async (submission) => {
  await initDB();
  checkStoreExists(SUBMISSIONS_STORE);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SUBMISSIONS_STORE], 'readwrite');
    const store = transaction.objectStore(SUBMISSIONS_STORE);
    const request = store.add({
      ...submission,
      synced: 0, // <-- CHANGED: Use 0 instead of false
      timestamp: new Date().toISOString()
    });

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// UPDATED: Mark a submission as synced
export const markSubmissionSynced = async (id) => {
  await initDB();
  checkStoreExists(SUBMISSIONS_STORE);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SUBMISSIONS_STORE], 'readwrite');
    const store = transaction.objectStore(SUBMISSIONS_STORE);
    const request = store.get(id);

    request.onsuccess = (event) => {
      const data = event.target.result;
      data.synced = 1; // <-- CHANGED: Use 1 instead of true
      const updateRequest = store.put(data);
      updateRequest.onsuccess = () => resolve();
      updateRequest.onerror = () => reject(updateRequest.error);
    };

    request.onerror = () => reject(request.error);
  });
};

// UPDATED: Get all unsynced submissions
export const getUnsyncedSubmissions = async () => {
  await initDB();
  checkStoreExists(SUBMISSIONS_STORE);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SUBMISSIONS_STORE], 'readonly');
    const store = transaction.objectStore(SUBMISSIONS_STORE);
    const index = store.index('synced');
    const request = index.openCursor(IDBKeyRange.only(0)); // <-- CHANGED: Use 0 instead of false

    const results = [];
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        results.push(cursor.value);
        cursor.continue();
      } else {
        resolve(results);
      }
    };

    request.onerror = () => reject(request.error);
  });
};


// Save questions offline
export const saveQuestionsOffline = async (questions, role) => {
  await initDB();
  checkStoreExists(QUESTIONS_STORE);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUESTIONS_STORE], 'readwrite');
    const store = transaction.objectStore(QUESTIONS_STORE);
    
    // Clear existing questions for this role first
    const clearRequest = store.index('role').openCursor(IDBKeyRange.only(role));
    
    clearRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const deleteRequest = cursor.delete();
        deleteRequest.onsuccess = () => {
          // After clearing, add new questions
          const addRequest = store.add({
            role: role,
            questions: questions,
            timestamp: new Date().toISOString()
          });
          
          addRequest.onsuccess = () => resolve(addRequest.result);
          addRequest.onerror = () => reject(addRequest.error);
        };
        deleteRequest.onerror = () => reject(deleteRequest.error);
      } else {
        // No existing questions, just add new ones
        const addRequest = store.add({
          role: role,
          questions: questions,
          timestamp: new Date().toISOString()
        });
        
        addRequest.onsuccess = () => resolve(addRequest.result);
        addRequest.onerror = () => reject(addRequest.error);
      }
    };
    
    clearRequest.onerror = () => reject(clearRequest.error);
  });
};

// Get questions offline
export const getQuestionsOffline = async (role) => {
  await initDB();
  checkStoreExists(QUESTIONS_STORE);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUESTIONS_STORE], 'readonly');
    const store = transaction.objectStore(QUESTIONS_STORE);
    const index = store.index('role');
    const request = index.openCursor(IDBKeyRange.only(role));

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        resolve(cursor.value.questions);
      } else {
        resolve([]); // Return empty array if no questions found
      }
    };

    request.onerror = () => reject(request.error);
  });
};

// NEW: Save PHCs offline
export const savePhcsOffline = async (phcs) => {
  await initDB();
  checkStoreExists(PHCS_STORE); // Use the helper to check
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PHCS_STORE], 'readwrite');
    const store = transaction.objectStore(PHCS_STORE);
    
    // Clear existing PHCs first to avoid duplicates
    const clearRequest = store.clear();

    clearRequest.onsuccess = () => {
      // Add all PHCs to the store
      phcs.forEach(phc => {
        store.add(phc);
      });
      resolve();
    };

    clearRequest.onerror = () => reject(clearRequest.error);
  });
};

// NEW: Get PHCs offline
export const getPhcsOffline = async () => {
  await initDB();
  checkStoreExists(PHCS_STORE); // Use the helper to check
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PHCS_STORE], 'readonly');
    const store = transaction.objectStore(PHCS_STORE);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

// Keep backward compatibility with old functions
export const saveResponseOffline = async (response) => {
  // ...existing code...
  return saveSubmissionOffline(response);
};

export const getUnsyncedResponses = async () => {
  // ...existing code...
  return getUnsyncedSubmissions();
};

export const markResponseSynced = async (id) => {
  // ...existing code...
  return markSubmissionSynced(id);
};

// NEW: Get all submissions from IndexedDB (both synced and unsynced)
export const getAllOfflineSubmissions = async () => {
  await initDB();
  checkStoreExists(SUBMISSIONS_STORE);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SUBMISSIONS_STORE], 'readonly');
    const store = transaction.objectStore(SUBMISSIONS_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      const submissions = request.result || [];
      // Add isOffline flag and format for consistency
      const formattedSubmissions = submissions.map(submission => ({
        ...submission,
        isOffline: true,
        submitted_at: submission.timestamp || submission.submitted_at
      }));
      resolve(formattedSubmissions);
    };

    request.onerror = () => reject(request.error);
  });
};

// NEW: Get all submissions (both online and offline)
export const getAllSubmissions = async () => {
  try {
    const deviceId = getDeviceId();
    
    // Fetch online submissions
    const onlineResponse = await fetch(`https://werise.duckdns.org/api/submission/?device_id=${deviceId}`);
    const onlineSubmissions = onlineResponse.ok ? await onlineResponse.json() : [];
    
    // Fetch offline submissions
    const offlineSubmissions = await getAllOfflineSubmissions();
    
    // Combine and mark submissions
    const allSubmissions = [
      ...offlineSubmissions.map(submission => ({
        ...submission,
        isOffline: true
      })),
      ...onlineSubmissions.map(submission => ({
        ...submission,
        isOffline: false
      }))
    ];
    
    // Sort by submission date (newest first)
    return allSubmissions.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
  } catch (error) {
    // ...existing code...
    // Return offline submissions only if online fetch fails
    return await getAllOfflineSubmissions();
  }
};

// NEW: Delete an offline submission
export const deleteOfflineSubmission = async (id) => {
  await initDB();
  checkStoreExists(SUBMISSIONS_STORE);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SUBMISSIONS_STORE], 'readwrite');
    const store = transaction.objectStore(SUBMISSIONS_STORE);
    const request = store.delete(id);

    request.onsuccess = () => {
      // ...existing code...
      resolve();
    };

    request.onerror = () => reject(request.error);
  });
};

// NEW: Clear all offline submissions
export const clearAllOfflineSubmissions = async () => {
  await initDB();
  checkStoreExists(SUBMISSIONS_STORE);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SUBMISSIONS_STORE], 'readwrite');
    const store = transaction.objectStore(SUBMISSIONS_STORE);
    const request = store.clear();

    request.onsuccess = () => {
      // ...existing code...
      resolve();
    };

    request.onerror = () => reject(request.error);
  });
};